import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "./tokens";

const useStyles = makeStyles({
  root: {
    position: "sticky",
    top: 0,
    zIndex: 20,
    display: "flex",
    height: "60px",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: BRAND.white,
    paddingLeft: "16px",
    paddingRight: "16px",
    ...shorthands.borderBottom("1px", "solid", "rgba(45,20,43,0.1)"),
    fontFamily: BRAND.fontPrimary,
    "@media (min-width: 640px)": {
      paddingLeft: "24px",
      paddingRight: "24px",
    },
  },
  left: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
    minWidth: 0,
  },
  right: {
    display: "flex",
    alignItems: "center",
    columnGap: "14px",
    minWidth: 0,
  },
  menuBtn: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: "transparent",
    backgroundImage: "none",
    ...shorthands.border("0"),
    width: "36px",
    height: "36px",
    ...shorthands.borderRadius("6px"),
    color: BRAND.purple,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    ":hover": {
      backgroundColor: "rgba(77,32,81,0.08)",
    },
  },
  /**
   * Bisheriges Verhalten: der Knopf verschwindet ab 1024 px Viewport. Die
   * Regel steckte fest in `menuBtn`; jetzt ist sie eine Variante, damit ein
   * Formular mit eigenem Verzeichnis-Auslöser sie abschalten kann.
   */
  menuBtnResponsive: {
    "@media (min-width: 1024px)": {
      display: "none",
    },
  },
  brand: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.4rem",
    color: BRAND.purple,
    letterSpacing: "-0.01em",
  },
  divider: {
    width: "1px",
    height: "20px",
    backgroundColor: "rgba(45,20,43,0.2)",
    display: "none",
    "@media (min-width: 640px)": {
      display: "block",
    },
  },
  subtitle: {
    fontSize: "0.9rem",
    color: "rgba(45,20,43,0.7)",
    display: "none",
    "@media (min-width: 640px)": {
      display: "block",
    },
  },
  customer: {
    fontWeight: 700,
    color: BRAND.ink,
    fontSize: "0.9rem",
    maxWidth: "55vw",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
});

type Props = {
  customerName?: string;
  onMenuClick?: () => void;
  /**
   * `"responsive"` (Default) = wie bisher: Knopf nur unter 1024 px.
   * `"always"` = immer sichtbar. `"never"` = gar nicht — für Formulare, die
   * das Inhaltsverzeichnis selbst auslöst (genau ein Auslöser je Kontext).
   */
  menuMode?: "responsive" | "always" | "never";
};

const TopBar = ({ customerName, onMenuClick, menuMode = "responsive" }: Props) => {
  const styles = useStyles();
  return (
    <header className={styles.root}>
      <div className={styles.left}>
        {menuMode !== "never" && (
        <button
          type="button"
          aria-label="Menü"
          onClick={onMenuClick}
          className={mergeClasses(
            styles.menuBtn,
            menuMode === "responsive" && styles.menuBtnResponsive,
          )}
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        )}
        <div className={styles.brand}>courté</div>
        <span className={styles.divider} aria-hidden="true" />
        <span className={styles.subtitle}>Kunden-Risikoanalyse</span>
      </div>
      <div className={styles.right}>
        {customerName && <div className={styles.customer}>{customerName}</div>}
      </div>
    </header>
  );
};

export default TopBar;
